import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment-timezone';
import { PureComponent } from 'react';

import { resolveTimezone } from '@/core/time';
import { darkBlue } from '@/theme/colors';

import Button from '../Button/Button';

interface InputProps {
  value?: moment.Moment | Date;
}

interface DateTimePickerProps {
  value?: Date;
  input?: InputProps;
  timezone?: string;
}

moment.updateLocale('en', {
  week: {
    dow: 1,
  },
});

const WALL_CLOCK = 'YYYY-MM-DDTHH:mm:ss';

// The pickers are configured with AdapterMoment (see App.tsx) and no `timezone`, so they read
// and write wall-clock fields in the browser's timezone. `timestamp` therefore always holds
// the viewer's wall clock carried in the browser's frame: getValue projects an instant into
// it, toViewerTimezone is its inverse, and nothing in between needs to know a timezone.
//
// The inverse is exact for every wall clock that exists exactly once in the browser's zone.
// One that lands in the browser's spring-forward gap cannot be carried — the engine resolves
// it with the pre-transition offset — so it displays displaced by the gap width. That is
// pre-existing and stable (it is still a fixed point, so no render loop); the cure is passing
// `timezone` to the pickers, which would delete this carrier outright.
function getValue(props: DateTimePickerProps) {
  const { value, input, timezone } = props;
  const stored = value ?? input?.value;
  if (!stored) {
    return undefined;
  }
  // An unparseable value would format to 'Invalid date', and `new Date('Invalid date')` is
  // truthy — so the field would report it has a value and hand both pickers a NaN moment.
  // Dropping it here leaves the field empty instead.
  const parsed = moment(stored);
  if (!parsed.isValid()) {
    return undefined;
  }
  return new Date(parsed.tz(resolveTimezone(timezone)).format(WALL_CLOCK));
}

function toViewerTimezone(wallClock: moment.Moment, timezone?: string) {
  return moment.tz(wallClock.format(WALL_CLOCK), resolveTimezone(timezone));
}

function getOnChange(props: any) {
  const { onChange, input } = props;
  return onChange || (input && input.onChange);
}

// getValue returns a fresh Date/moment on every call, so comparing references is
// meaningless — compare the instant each points at (treating empty/undefined as "no value").
function isSameInstant(a: any, b: any) {
  const av = a ? moment(a).valueOf() : null;
  const bv = b ? moment(b).valueOf() : null;
  return av === bv;
}

function getError(props: any) {
  const { errorText, meta } = props;
  return errorText || (meta && (meta.touched || meta.modified) && meta.error);
}

function getWarning(props: any) {
  const { warningText, meta, displayWarning } = props;
  return warningText || (displayWarning && meta && meta.data.warning);
}

class DateTimePicker extends PureComponent {
  constructor(props: any) {
    super(props);

    const value = getValue(props);
    this.state = {
      timestamp: value,
      haveDate: !!value,
      haveTime: !!value,
    };
  }

  UNSAFE_componentWillReceiveProps(props: any) {
    const value = getValue(props);
    // Skip the update when the value hasn't actually changed. getValue mints a new
    // object every call, so an unconditional setState re-renders this PureComponent on
    // every parent render and never settles — under a form that re-renders repeatedly
    // (react-final-form hands a fresh `input` each time) that trips React's nested update
    // limit ("Maximum update depth exceeded"). (Sentry WEBAPP-CC0)
    // @ts-expect-error TS(2339): Property 'timestamp' does not exist on type 'Readonly<{}>'.
    if (isSameInstant(value, this.state.timestamp)) return;
    this.setState({
      timestamp: value,
      haveDate: !!value,
      haveTime: !!value,
    });
  }

  handleOnChangeDate = (e: any) => {
    const onChange = getOnChange(this.props);
    // @ts-expect-error TS(2339): Property 'haveTime' does not exist on type 'Readon... Remove this comment to see the full error message
    const { haveTime } = this.state;
    // @ts-expect-error TS(2339): Property 'minDate' does not exist on type 'Readonl... Remove this comment to see the full error message
    const { minDate, dateOnly, timezone } = this.props;

    if (!e) {
      this.handleClear();
      return;
    }

    // Ignore invalid/incomplete input (e.g. a half-typed date) instead of
    // forwarding a NaN moment to consumers, which crashes downstream date math
    // such as TimeRange.duration(). (Sentry WEBAPP-BWQ)
    if (!e.isValid()) return;

    // Workaround bug that does not validate min date when navigating to the next month
    if (minDate && e.isBefore(minDate)) return;

    // Keep the wall clock the picker emitted: the day the user clicked, at the time the
    // field already showed. Turning it into an instant and re-reading that instant in the
    // viewer's timezone rolled the date whenever the two timezones straddled midnight, so
    // the field showed the day after the one that was clicked. (ENG-5590)
    const wallClock = (dateOnly ? e.clone().startOf('day') : e.clone()).second(0).millisecond(0);

    this.setState({
      haveDate: true,
      timestamp: wallClock,
    });

    if ((haveTime || dateOnly) && onChange) {
      onChange(toViewerTimezone(wallClock, timezone));
    }
  };

  handleOnChangeTime = (e: any) => {
    const onChange = getOnChange(this.props);
    // @ts-expect-error TS(2339): Property 'timestamp' does not exist on type 'Reado... Remove this comment to see the full error message
    const { timestamp, haveDate } = this.state;
    // @ts-expect-error TS(2339): Property 'timezone' does not exist on type 'Readon... Remove this comment to see the full error message
    const { timezone } = this.props;

    // The picker emits null when a section is cleared and an invalid moment for
    // incomplete input; bail out instead of dereferencing an undefined/NaN date,
    // mirroring the guard in handleOnChangeDate. (Sentry WEBAPP-BWP)
    if (!e) {
      this.handleClear();
      return;
    }
    if (!e.isValid()) return;

    // The clock edits only the clock: keep the day already in state and take just the hour
    // and minute the picker emitted. Deriving the day from the state read back in the
    // viewer's timezone moved the date off the one the user picked. (ENG-5590)
    const wallClock = (timestamp ? moment(timestamp) : e.clone())
      .hour(e.hour())
      .minute(e.minute())
      .second(0)
      .millisecond(0);

    this.setState({
      haveTime: true,
      timestamp: wallClock,
    });

    if (haveDate && onChange) {
      onChange(toViewerTimezone(wallClock, timezone));
    }
  };

  handleClear = () => {
    const onChange = getOnChange(this.props);
    this.setState({
      haveTime: false,
      haveDate: false,
      timestamp: '',
    });
    if (onChange) onChange(null);
  };

  formatLabel = (e: any, invalid: any, pattern: any) => {
    const date = moment(e);
    return date.isValid() ? date.format(pattern) : (invalid ?? '');
  };

  render() {
    const {
      // @ts-expect-error TS(2339): Property 'name' does not exist on type 'Readonly<{... Remove this comment to see the full error message
      name,
      // @ts-expect-error TS(2339): Property 'label' does not exist on type 'Readonly<... Remove this comment to see the full error message
      label,
      // @ts-expect-error TS(2339): Property 'placeholder' does not exist on type 'Rea... Remove this comment to see the full error message
      placeholder,
      // @ts-expect-error TS(2339): Property 'buttonClear' does not exist on type 'Rea... Remove this comment to see the full error message
      buttonClear = true,
      // @ts-expect-error TS(2339): Property 'fullWidth' does not exist on type 'Reado... Remove this comment to see the full error message
      fullWidth = false,
      // @ts-expect-error TS(2339): Property 'clearable' does not exist on type 'Reado... Remove this comment to see the full error message
      clearable,
      // @ts-expect-error TS(2339): Property 'minDate' does not exist on type 'Readonl... Remove this comment to see the full error message
      minDate,
      // @ts-expect-error TS(2339): Property 'maxDate' does not exist on type 'Readonl... Remove this comment to see the full error message
      maxDate,
      // @ts-expect-error TS(2339): Property 'dateOnly' does not exist on type 'Readon... Remove this comment to see the full error message
      dateOnly,
      // @ts-expect-error TS(2339): Property 'required' does not exist on type 'Readon... Remove this comment to see the full error message
      required,
      // @ts-expect-error TS(2339): Property 'labelDateFormat' does not exist on type ... Remove this comment to see the full error message
      labelDateFormat = 'MMM DD YYYY',
    } = this.props;
    // @ts-expect-error TS(2339): Property 'timestamp' does not exist on type 'Reado... Remove this comment to see the full error message
    const { timestamp, haveTime, haveDate } = this.state;

    const errorText = getError(this.props);
    const warningText = getWarning(this.props);

    const style = {
      display: 'flex',
      alignItems: 'center',
      // @ts-expect-error TS(2339): Property 'style' does not exist on type 'Readonly<... Remove this comment to see the full error message
      ...this.props.style,
    };

    const hasError = !!(errorText || warningText);

    const textFieldsProps = {
      fullWidth,
      error: hasError,
      placeholder,
      helperText: hasError ? errorText || warningText : '',
      required,
    };

    const slotProps = {
      textField: textFieldsProps,
      field: { clearable },
      inputAdornment: { position: 'start' },
    };

    return (
      <div style={style}>
        <DatePicker
          name={name}
          value={haveDate ? moment(timestamp) : null}
          label={label}
          minDate={minDate ? moment(minDate) : undefined}
          maxDate={maxDate ? moment(maxDate) : undefined}
          onChange={this.handleOnChangeDate}
          format={labelDateFormat}
          required={required}
          // @ts-expect-error TS(2322): Type '{ textField: { fullWidth: any; error: boolea... Remove this comment to see the full error message
          slotProps={slotProps}
        />
        {!dateOnly && (
          <TimePicker
            name={name}
            value={haveTime ? moment(timestamp) : null}
            label={label}
            onChange={this.handleOnChangeTime}
            minutesStep={5}
            required={required}
            sx={{ marginLeft: 2 }}
            format="h:mm a"
            // @ts-expect-error TS(2322): Type '{ textField: { fullWidth: any; error: boolea... Remove this comment to see the full error message
            slotProps={slotProps}
          />
        )}
        {buttonClear && (
          <Button variant="text" size="tiny" fontColor={darkBlue} onClick={this.handleClear}>
            Clear
          </Button>
        )}
      </div>
    );
  }
}

export default DateTimePicker;
